/**
 * Recurring interface strings that used to be hardcoded in components.
 *
 * Anything a visitor reads which is not page-specific content belongs here, so
 * it can be translated per language. Section numbers refer to the nsave Arabic
 * Localization Pack.
 */
/**
 * Every currency code the calculator and the comparison table can put in front
 * of a visitor: the three send currencies of the compare page plus the payout
 * currencies of `RECEIVE_CURRENCIES`. Keeping it a closed union means adding a
 * payout corridor fails typecheck until the currency has a name in every
 * language, rather than rendering a blank selector row.
 */
export type DisplayCurrency =
  | 'GBP'
  | 'USD'
  | 'EUR'
  | 'EGP'
  | 'BDT'
  | 'PKR'
  | 'NGN'
  | 'MAD'
  | 'TRY'
  | 'SYP';

export interface UiStrings {
  /**
   * Newsletter form (pack section 24).
   *
   * The status strings mirror the `NewsletterResultCode` values the
   * `/api/newsletter` route returns. The route answers in English regardless of
   * the page language, so the client maps the code to one of these rather than
   * rendering the response message.
   */
  newsletter: {
    emailPlaceholder: string;
    emailLabel: string;
    subscribe: string;
    subscribing: string;
    success: string;
    alreadySubscribed: string;
    invalidEmail: string;
    unavailable: string;
    error: string;
  };
  /** Footer regional-variant and language switchers. */
  footer: {
    regionalVersionsLabel: string;
    languageLabel: string;
  };
  /** Pricing page chrome (heading, audience toggle, terms link). */
  pricing: {
    heading: string;
    personal: string;
    business: string;
    seeAllTerms: string;
  };
  /** Business page chrome. */
  business: {
    testimonialsTitle: string;
  };
  /** Dismiss control shared by the dialogs. */
  close: string;
  /** Quote-carousel controls — announced to assistive tech only. */
  testimonials: {
    previous: string;
    next: string;
    rating: string;
  };
  /** In-card copy for the FX calculator widget. */
  calculator: {
    youSend: string;
    recipientGets: string;
    exchangeRate: string;
    fees: string;
    amountPlaceholder: string;
    amountLabel: string;
    back: string;
    selectCurrency: string;
    selectPlan: string;
    transferFee: string;
    amount: string;
    amountTooLow: string;
    /** Full plan label, so each language controls its own word order. */
    plans: { FREE: string; PRO: string };
    changeCurrency: string;
    changePlan: string;
    /** Selector labels for the comparison table's send / receive pair. */
    from: string;
    to: string;
    /** Selector display name for each currency, shown under its code. */
    currencyNames: Record<DisplayCurrency, string>;
    /** Shown when the live competitor comparison is unreachable. */
    demoRatesNotice: string;
    /** Leads the "<prefix> 1,234 EGP" savings line under the nsave column. */
    saveUpTo: string;
  };
  /** Desktop "scan to install" callout next to the app QR code. */
  downloadQr: {
    label: string;
  };
  /** Header controls that are not driven by the nav content lists. */
  header: {
    learn: string;
    homeLabel: string;
    mainNavLabel: string;
  };
  /**
   * Cookie consent banner.
   *
   * ⚠️ The Arabic here is authored, not supplied: the localization pack lists the
   * cookie banner as something to re-check in Arabic (checklist item 10) but gives
   * no copy for it. Needs a native review pass.
   */
  cookies: {
    dialogLabel: string;
    title: string;
    bodyBeforeLink: string;
    privacyLinkLabel: string;
    bodyAfterLink: string;
    accept: string;
    reject: string;
  };
  /**
   * Footer legal block. Modern Standard Arabic is required here per the pack's
   * language rules, and the Arabic version is an unapproved draft.
   */
  legal: {
    copyright: (year: number) => string;
    paragraphs: string[];
  };
}

export const ui: UiStrings = {
  newsletter: {
    emailPlaceholder: 'Enter your email',
    emailLabel: 'Email address',
    subscribe: 'Subscribe',
    subscribing: 'Subscribing…',
    success: 'Thanks for subscribing.',
    alreadySubscribed: "You're already subscribed.",
    invalidEmail: 'Enter a valid email address.',
    unavailable: 'Newsletter signup is temporarily unavailable. Please try again.',
    error: 'We could not subscribe you. Please try again.',
  },
  footer: {
    regionalVersionsLabel: 'Regional versions',
    languageLabel: 'Language',
  },
  pricing: {
    heading: 'Keep Your Earnings',
    personal: 'Personal',
    business: 'Business',
    seeAllTerms: 'See all terms & conditions',
  },
  business: {
    testimonialsTitle: 'Trusted by businesses around the world',
  },
  close: 'Close',
  testimonials: {
    previous: 'Previous testimonials',
    next: 'Next testimonials',
    rating: '5 out of 5 stars',
  },
  calculator: {
    youSend: 'You send',
    recipientGets: 'Recipient gets',
    exchangeRate: 'Exchange rate',
    fees: 'Fees',
    amountPlaceholder: 'Enter amount',
    amountLabel: 'Amount to send',
    back: 'Back',
    selectCurrency: 'Select currency',
    selectPlan: 'Select plan',
    transferFee: 'Transfer fee',
    amount: 'Amount',
    amountTooLow: 'Amount too low',
    plans: { PRO: 'Pro Plan', FREE: 'Free Plan' },
    changeCurrency: 'Change currency, currently',
    changePlan: 'Change plan, currently',
    from: 'From',
    to: 'To',
    currencyNames: {
      GBP: 'British Pound',
      USD: 'US Dollar',
      EUR: 'Euro',
      EGP: 'Egyptian Pound',
      BDT: 'Bangladeshi Taka',
      PKR: 'Pakistani Rupee',
      NGN: 'Nigerian Naira',
      MAD: 'Moroccan Dirham',
      TRY: 'Turkish Lira',
      SYP: 'Syrian Pound',
    },
    demoRatesNotice: 'Demo competitor rates are shown because live comparison data is unavailable.',
    saveUpTo: 'Save up to',
  },
  downloadQr: {
    label: 'Download nsave',
  },
  header: {
    learn: 'Learn',
    homeLabel: 'nsave home',
    mainNavLabel: 'Main',
  },
  cookies: {
    dialogLabel: 'Cookie preferences',
    title: 'We use cookies',
    bodyBeforeLink:
      'We use cookies and similar technologies to analyse traffic and improve nsave. Read our ',
    privacyLinkLabel: 'privacy policy',
    bodyAfterLink: '.',
    accept: 'Accept',
    reject: 'Reject',
  },
  legal: {
    copyright: (year) => `Copyright © ${year} nsave Ltd. All rights reserved.`,
    paragraphs: [
      'This website is owned and operated by nsave Ltd., incorporated in the United Kingdom. The information provided is not legally binding and does not constitute an offering, endorsement, recommendation or solicitation to enter into any type of financial transaction in this or in any other jurisdiction in which such solicitation or offer would be unlawful under the laws of such jurisdiction.',
      'npay Inc. is registered in the United States in the State of Delaware. npay Inc. partners with licensed financial institutions to provide certain services such as the provision of cards and is a technology services provider, not a bank.',
      'npay USA Inc. is incorporated in Montana, USA and registered as a Money Services Business (“MSB”) with the Financial Crimes Enforcement Network (“FinCEN”) under MSB Registration Number 31000315267589',
      'GBP and Multi-Currency accounts are offered by Keel Money Ltd; an Electronic Money Institution authorised by the Financial Conduct Authority under the Electronic Money Regulations 2011 (FRN 1020783). Client funds are safeguarded in UK- or EEA-authorised credit institutions but are not protected by the Financial Services Compensation Scheme.',
      'nsave ltd is appointed under Regulation 33 of the EMRs to distribute and/or redeem electronic money on behalf of Keel Money Ltd and is not itself authorised to issue electronic money or provide payment services. More details can be found in the Keel End-User T&Cs, which you must agree to before using any services provided by Keel Money Ltd.',
    ],
  },
};
